/**
 * InlineTextEdit — a single-line value that turns into a text field in place.
 *
 * Used by the work-order customer card (#2105) so contact details are edited
 * where they are read instead of behind a dialog. Follows the same interaction
 * as InlineMileageEdit, but commits through explicit confirm/cancel buttons
 * because a blur can land on those buttons themselves.
 */

import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { CopyButton } from '@/components/common/CopyButton';

interface InlineTextEditProps {
  /** Translated field name — used for the input label and the pencil's aria-label. */
  label: string;
  value: string | null;
  onSave: (value: string) => void | Promise<void>;
  saving?: boolean;
  type?: 'text' | 'tel' | 'email';
  placeholder?: string;
  /** Rendered next to the value in read mode (e.g. an "overridden" hint icon). */
  adornment?: ReactNode;
  /** Show a copy-to-clipboard icon next to the value, separate from edit (#2671). */
  copyable?: boolean;
}

export function InlineTextEdit({
  label,
  value,
  onSave,
  saving = false,
  type = 'text',
  placeholder = '—',
  adornment,
  copyable = false,
}: InlineTextEditProps) {
  const { t: tc } = useTranslation('common');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const startEditing = () => {
    setDraft(value ?? '');
    setEditing(true);
    setTimeout(() => { inputRef.current?.focus(); }, 50);
  };

  const handleSave = () => {
    setEditing(false);
    if (draft.trim() === (value ?? '').trim()) return;
    void onSave(draft.trim());
  };

  const handleCancel = () => { setEditing(false); };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSave();
    }
    if (event.key === 'Escape') handleCancel();
  };

  if (editing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
        <TextField
          inputRef={inputRef}
          size="small"
          type={type}
          label={label}
          value={draft}
          onChange={(event) => { setDraft(event.target.value); }}
          onKeyDown={handleKeyDown}
          disabled={saving}
          fullWidth
        />
        <Tooltip title={tc('save')}>
          <span>
            <IconButton size="small" onClick={handleSave} disabled={saving} aria-label={tc('save')}>
              {saving ? <CircularProgress size={16} /> : <CheckIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={tc('cancel')}>
          <span>
            <IconButton size="small" onClick={handleCancel} disabled={saving} aria-label={tc('cancel')}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
      <Typography variant="body2" noWrap sx={{ color: value ? 'text.primary' : 'text.disabled' }}>
        {value && value.length > 0 ? value : placeholder}
      </Typography>
      {adornment}
      {copyable && (
        <CopyButton value={value} label={tc('copyField', { field: label })} />
      )}
      <Tooltip title={label}>
        <IconButton size="small" onClick={startEditing} aria-label={tc('editField', { field: label })}>
          <EditIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
